import {
  Discount,
  DiscountSourceType,
  DiscountScope as DiscountScopeV2,
  DiscountScopeWithLiterals,
  DiscountSourceTypeWithLiterals,
} from '@wix/auto_sdk_ecom_cart-v-2';
import {AppliedDiscount, AppliedDiscountDiscountType} from '@wix/auto_sdk_ecom_current-cart';
import {ModelContext} from '../../../types/app.types';
import {SPECS} from '../../specs';

export class DiscountModel {
  public name: string;
  public scope: DiscountScope;
  public sourceType: DiscountSource;
  public totalAmountOff: string;
  public targetLineItemIds: string[];

  constructor(params: DiscountModel) {
    this.name = params.name;
    this.scope = params.scope;
    this.totalAmountOff = params.totalAmountOff;
    this.targetLineItemIds = params.targetLineItemIds;
    this.sourceType = params.sourceType;
  }

  public static fromCartV1(appliedDiscounts: AppliedDiscount[], totalAmountOff: string) {
    const discount = appliedDiscounts?.find((d) => d?.coupon);
    if (!discount?.coupon) {
      return undefined;
    }

    return new DiscountModel({
      name: discount.coupon.code,
      scope:
        discount?.discountType === AppliedDiscountDiscountType.SHIPPING
          ? DiscountScope.DELIVERY
          : DiscountScope.CART_SUBTOTAL,
      totalAmountOff,
      targetLineItemIds: [],
      sourceType: DiscountSource.COUPON,
    });
  }

  public static fromCartV2(discount: Discount, modelContext: ModelContext) {
    return new DiscountModel({
      name: discount.source?.couponCode ?? discount.name.translated ?? discount.name.original,
      scope: toCouponDiscountScope(discount.scope),
      totalAmountOff: modelContext.formatAmount(discount.total?.convertedAmount),
      targetLineItemIds: modelContext.viewerContext.isExperimentEnabled(SPECS.UseLineItemDiscountInCartSummary)
        ? discount.lineItemDiscounts?.map((lid) => lid.lineItemId) ?? []
        : discount.benefits.map((benefit) => benefit.lineItemId),
      sourceType: toDiscountSource(discount.source?.sourceType),
    });
  }
}

export enum DiscountScope {
  CART_SUBTOTAL = 'CART_SUBTOTAL',
  LINE_ITEM = 'LINE_ITEM',
  DELIVERY = 'DELIVERY',
}

export enum DiscountSource {
  COUPON = 'COUPON',
  AUTOMATIC_RULE = 'AUTOMATIC_RULE',
  UNKNOWN_DISCOUNT_SOURCE_TYPE = 'UNKNOWN_DISCOUNT_SOURCE_TYPE',
}

const toDiscountSource = (discountSource: DiscountSourceTypeWithLiterals) => {
  switch (discountSource) {
    case DiscountSourceType.COUPON:
      return DiscountSource.COUPON;
    case DiscountSourceType.AUTOMATIC_RULE:
      return DiscountSource.AUTOMATIC_RULE;
  }
};

const toCouponDiscountScope = (discountScope: DiscountScopeWithLiterals) => {
  switch (discountScope) {
    case DiscountScopeV2.DELIVERY:
      return DiscountScope.DELIVERY;
    case DiscountScopeV2.LINE_ITEM:
      return DiscountScope.LINE_ITEM;
    case DiscountScopeV2.CART_SUBTOTAL:
    case DiscountScopeV2.UNKNOWN_DISCOUNT_SCOPE:
      return DiscountScope.CART_SUBTOTAL;
  }
};
